import {
    type AeroOptimizationJobInputState,
    createDefaultAutoPartialExitState
} from '@/features/tradeLogicAuthoring/components/aeroOptimizationJobInput';

/** The exit-strategy slice of the job form that a preset replaces. */
export type AeroExitStrategyFields = Pick<
    AeroOptimizationJobInputState,
    | 'stopLossEnabled'
    | 'stopLossBasis'
    | 'stopLossPercentages'
    | 'trailingStopEnabled'
    | 'trailingActivationProfits'
    | 'trailingByPercentages'
    | 'profitTargetEnabled'
    | 'profitTargetPercentages'
    | 'partialExitMode'
    | 'manualPartialExits'
    | 'autoPartialExits'
    | 'profitFloorEnabled'
    | 'profitFloors'
>;

export interface AeroPreset {
    id: string;
    label: string;
    description: string;
    values: AeroExitStrategyFields;
}

export const AERO_PRESETS: AeroPreset[] = [
    {
        id: 'conservative',
        label: 'Conservative',
        description: 'Tight stops, modest targets — a fast, focused search.',
        values: {
            stopLossEnabled: true,
            stopLossBasis: 'openingPremium',
            stopLossPercentages: [3, 5],
            trailingStopEnabled: false,
            trailingActivationProfits: [],
            trailingByPercentages: [],
            profitTargetEnabled: true,
            profitTargetPercentages: [8, 12],
            partialExitMode: 'none',
            manualPartialExits: [],
            autoPartialExits: createDefaultAutoPartialExitState(),
            profitFloorEnabled: false,
            profitFloors: []
        }
    },
    {
        id: 'balanced',
        label: 'Balanced',
        description: 'Trailing stops, manual partial exits, and a profit floor.',
        values: {
            stopLossEnabled: true,
            stopLossBasis: 'openingPremium',
            stopLossPercentages: [5, 7, 10],
            trailingStopEnabled: true,
            trailingActivationProfits: [8],
            trailingByPercentages: [3, 5],
            profitTargetEnabled: true,
            profitTargetPercentages: [12, 18, 25],
            partialExitMode: 'manual',
            manualPartialExits: [
                { atProfit: '8', exitPercentage: '25' },
                { atProfit: '15', exitPercentage: '50' }
            ],
            autoPartialExits: createDefaultAutoPartialExitState(),
            profitFloorEnabled: true,
            profitFloors: [{ triggerPercentage: '15', sellAtPercentage: '8' }]
        }
    },
    {
        id: 'wide',
        label: 'Wide sweep',
        description: 'Broad ranges with auto-generated partial-exit ladders.',
        values: {
            stopLossEnabled: true,
            stopLossBasis: 'openingPremium',
            stopLossPercentages: [3, 5, 7, 10, 15],
            trailingStopEnabled: true,
            trailingActivationProfits: [5, 10],
            trailingByPercentages: [2, 3, 5],
            profitTargetEnabled: true,
            profitTargetPercentages: [10, 15, 20, 25, 30],
            partialExitMode: 'auto',
            manualPartialExits: [],
            autoPartialExits: {
                atProfitFrom: '5',
                atProfitTo: '20',
                atProfitStep: '5',
                exitPercentages: [25, 50],
                maxLevels: 2,
                minSpacingPercent: '',
                capPerTarget: '',
                includeRemainderAtTarget: true
            },
            profitFloorEnabled: true,
            profitFloors: [
                { triggerPercentage: '15', sellAtPercentage: '8' },
                { triggerPercentage: '25', sellAtPercentage: '15' }
            ]
        }
    }
];
